import { Link } from "react-router-dom";
import { BookOpen, Camera, Flame, FolderHeart, Sparkles, Trophy, Zap } from "lucide-react";

import { CriticalButton as Button } from "@/components/layout/CriticalButton";
import {
  CriticalCard as Card,
  CriticalCardContent as CardContent,
  CriticalSkeleton as Skeleton,
} from "@/components/layout/CriticalSurface";
import { useAuth } from "@/contexts/AuthContext";
import { useUserXP } from "@/hooks/useGamification";
import { trackEvent } from "@/lib/analytics";

const actions = [
  { href: "/identify", label: "Identify another specimen", icon: Camera },
  { href: "/training", label: "Continue training", icon: BookOpen },
  { href: "/my-finds", label: "Open My Finds", icon: FolderHeart },
] as const;

export function ReturningMemberHub() {
  const { user, loading: authLoading } = useAuth();
  const { data: userXP, isLoading: xpLoading } = useUserXP();

  if (authLoading) {
    return (
      <Card
        aria-label="Loading your GeoMiner field desk"
        className="min-h-[29rem] overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-card to-accent/10 md:min-h-[13.5rem]"
        data-home-hub-state="loading"
      >
        <CardContent className="p-5 md:p-7">
          <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <Skeleton className="mb-3 h-3 w-40" />
              <Skeleton className="h-8 w-72 max-w-full" />
              <Skeleton className="mt-3 h-4 w-full max-w-2xl" />
              <Skeleton className="mt-2 h-4 w-4/5 max-w-xl" />
              <div className="mt-4 flex flex-wrap gap-2">
                <Skeleton round className="h-8 w-28" />
                <Skeleton round className="h-8 w-32" />
                <Skeleton round className="h-8 w-36" />
              </div>
            </div>
            <div className="flex flex-col gap-2 md:w-64">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card
        className="min-h-[29rem] overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-card to-accent/10 md:min-h-[13.5rem]"
        data-home-hub-state="guest"
      >
        <CardContent className="p-5 md:p-7">
          <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent">Your free GeoMiner field desk</p>
              <h2 className="font-display text-2xl font-bold">Keep every find and field skill together.</h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                Create a free account to save photo identifications, keep your training progress, and return to your geology work from any device.
              </p>

              <div className="mt-4 flex flex-wrap gap-2 text-sm" aria-label="Free GeoMiner account benefits">
                <span className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 font-medium">
                  <FolderHeart className="h-4 w-4 text-primary" /> Save My Finds
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 font-medium">
                  <Zap className="h-4 w-4 text-accent" /> Track learning
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 font-medium">
                  <Sparkles className="h-4 w-4 text-primary" /> Always free to start
                </span>
              </div>
            </div>

            <div className="flex min-w-0 flex-col gap-2 md:w-64">
              <Button asChild align="start">
                <Link
                  to="/account?mode=signup&redirect=/"
                  onClick={() => trackEvent("home_cta", { cta: "/account", placement: "return_hub" })}
                >
                  <Sparkles className="h-4 w-4" /> Create free account
                </Link>
              </Button>
              <Button asChild variant="outline" align="start">
                <Link
                  to="/quick-id"
                  onClick={() => trackEvent("home_cta", { cta: "/quick-id", placement: "return_hub" })}
                >
                  <Camera className="h-4 w-4" /> Try Quick ID instantly
                </Link>
              </Button>
              <Button asChild variant="outline" align="start">
                <Link
                  to="/training"
                  onClick={() => trackEvent("home_cta", { cta: "/training", placement: "return_hub" })}
                >
                  <BookOpen className="h-4 w-4" /> Explore free training
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const streak = userXP?.current_streak ?? 0;
  const totalXP = userXP?.total_xp ?? 0;
  const level = userXP?.level ?? 1;

  return (
    <Card
      className="min-h-[29rem] overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-card to-accent/10 md:min-h-[13.5rem]"
      data-home-hub-state="member"
    >
      <CardContent className="p-5 md:p-7">
        <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-accent">Your GeoMiner field desk</p>
            <h2 className="font-display text-2xl font-bold">Welcome back to the field.</h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              Keep building your specimen collection and geology skills. Your finds, progress, and achievements are ready where you left them.
            </p>

            <div className="mt-4 flex flex-wrap gap-2 text-sm" aria-label="Your GeoMiner progress">
              <span className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 font-medium">
                <Flame className="h-4 w-4 text-accent" />
                {streak > 0 ? `${streak}-day streak` : "Start today's streak"}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 font-medium">
                <Zap className="h-4 w-4 text-primary" />
                {xpLoading ? "Loading XP…" : `${totalXP} XP · Level ${level}`}
              </span>
              <Link
                to="/leaderboard"
                onClick={() => trackEvent("home_cta", { cta: "/leaderboard", placement: "return_hub" })}
                className="inline-flex items-center gap-1.5 rounded-full border bg-background/70 px-3 py-1.5 font-medium transition-colors hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Trophy className="h-4 w-4" /> View leaderboard
              </Link>
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-2 md:w-64">
            {actions.map(({ href, label, icon: Icon }, index) => (
              <Button key={href} asChild variant={index === 0 ? "default" : "outline"} align="start">
                <Link
                  to={href}
                  onClick={() => trackEvent("home_cta", { cta: href, placement: "return_hub" })}
                >
                  <Icon className="h-4 w-4" /> {label}
                </Link>
              </Button>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
